import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  ShoppingCart,
  Boxes,
  ArrowRight,
} from 'lucide-react';
import { Card, StatCard, Badge, Tabs, Alert, Spinner, Button } from '@ui';
import { getDashboardApi } from '../services/api';
import { formatIDR, formatNumber, formatLongDate } from '../utils/format';

const UNSOLD_LIMIT = 10;
const DUE_LIMIT = 5;

function StatValue({ main, sub }) {
  return (
    <span className="dashboard-stat">
      <span className="dashboard-stat__main">{main}</span>
      <span className="dashboard-stat__sub">{sub}</span>
    </span>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeDebtTab, setActiveDebtTab] = useState('hutang');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const payload = await getDashboardApi();
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) setLoadError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const retryLoad = () => {
    setLoading(true);
    setLoadError(null);
    setReloadKey((k) => k + 1);
  };

  const stats = data?.stats ?? null;
  const unsold = data?.unsold ?? [];
  const due = data?.due ?? {
    piutang: [],
    hutang: [],
    counts: { piutang: 0, hutang: 0, total: 0 },
  };
  const dueTotals = {
    hutang: due.hutang.reduce((sum, r) => sum + r.amount, 0),
    piutang: due.piutang.reduce((sum, r) => sum + r.amount, 0),
  };

  const unsoldItems = unsold.slice(0, UNSOLD_LIMIT);
  const activeDueItems =
    (activeDebtTab === 'hutang' ? due.hutang : due.piutang).slice(0, DUE_LIMIT);

  const content = loading || !data ? (
    <div className="pa-panel-loading">
      <Spinner />
      <div className="purchase-empty__hint">Memuat ringkasan…</div>
    </div>
  ) : loadError ? (
    <div className="pa-panel-loading">
      <div className="purchase-empty__hint">{loadError.message}</div>
      <Button variant="secondary" size="sm" onClick={retryLoad}>
        Coba Lagi
      </Button>
    </div>
  ) : (
    <>
      <div className="dashboard-page__stats">
        <StatCard
          label="Terjual Hari Ini"
          value={
            <StatValue
              main={`${formatNumber(stats.sales.count)} transaksi`}
              sub={formatIDR(stats.sales.value)}
            />
          }
          icon={TrendingUp}
          iconVariant="green"
        />
        <StatCard
          label="Pembelian Hari Ini"
          value={
            <StatValue
              main={`${formatNumber(stats.purchases.count)} transaksi`}
              sub={formatIDR(stats.purchases.value)}
            />
          }
          icon={ShoppingCart}
          iconVariant="navy"
        />
      </div>

      <div className="dashboard-alerts">
        <Card
          title="On Stock"
          subtitle={`Total · ${formatNumber(unsold.length)} SKU belum ada penjualan`}
        >
          {unsoldItems.length ? (
            <ul className="alert-list">
              {unsoldItems.map((item) => (
                <li key={item.sku_code} className="alert-list__item">
                  <div className="alert-list__info">
                    <span className="alert-list__name">{item.product_name}</span>
                    <span className="alert-list__code">
                      {item.sku_code} ·{' '}
                      {item.lastSaleDate
                        ? `terakhir terjual ${formatLongDate(item.lastSaleDate)}`
                        : 'belum pernah terjual'}
                    </span>
                  </div>
                  <Badge variant="navy">
                    <Boxes size={12} />
                    {formatNumber(item.stock)} {item.unit}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <div className="alert-list__empty">
              Semua produk berstok sudah terjual.
            </div>
          )}
          <Link className="alert-list__more" to="/reports">
            Lihat semua ({formatNumber(unsold.length)})
            <ArrowRight size={14} />
          </Link>
        </Card>

        <Card
          title="Hutang &amp; Piutang"
          subtitle={`Jatuh tempo · ${formatNumber(due.counts.total)} tagihan`}
        >
          <div className="debt-summary">
            <Alert type="danger" title="Total Hutang">
              <div className="debt-summary__meta">
                <span className="debt-summary__value">
                  {formatIDR(dueTotals.hutang)}
                </span>
                <span className="debt-summary__count">
                  {formatNumber(due.counts.hutang)} tagihan
                </span>
              </div>
            </Alert>
            <Alert type="warning" title="Total Piutang">
              <div className="debt-summary__meta">
                <span className="debt-summary__value">
                  {formatIDR(dueTotals.piutang)}
                </span>
                <span className="debt-summary__count">
                  {formatNumber(due.counts.piutang)} tagihan
                </span>
              </div>
            </Alert>
          </div>

          <Tabs
            tabs={[
              { id: 'hutang', label: 'Hutang', badge: due.counts.hutang },
              { id: 'piutang', label: 'Piutang', badge: due.counts.piutang },
            ]}
            activeTab={activeDebtTab}
            onChange={setActiveDebtTab}
          />

          <div className="alert-list">
            {activeDueItems.length ? (
              activeDueItems.map((item) => (
                <div
                  key={`${activeDebtTab}-${item.trxno}`}
                  className="alert-list__item"
                >
                  <div className="alert-list__info">
                    <span className="alert-list__name">{item.party}</span>
                    <span className="alert-list__code">
                      {item.trxno} · jatuh tempo{' '}
                      {item.dueDate ? formatLongDate(item.dueDate) : '—'}
                    </span>
                  </div>
                  <div className="alert-list__right">
                    <span className="alert-list__amount">
                      {formatIDR(item.amount)}
                    </span>
                    <Badge variant={item.overdue ? 'danger' : 'warning'}>
                      {item.overdue ? 'Terlambat' : 'Akan tiba'}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="alert-list__empty">
                Tidak ada {activeDebtTab === 'hutang' ? 'hutang' : 'piutang'}{' '}
                jatuh tempo.
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__toolbar">
        <div className="dashboard-page__greeting">
          <h2>Ringkasan Stok &amp; Penjualan</h2>
          <p>Data hingga {data ? formatLongDate(data.asOf) : '…'}</p>
        </div>
        <Link className="dashboard-page__cta" to="/reports">
          Lihat Laporan
          <ArrowRight size={14} />
        </Link>
      </div>

      {content}
    </div>
  );
}
